/**
 * fetch-based replacement for $.get / $.post / $.ajax / jquery-form / .load().
 *
 * Requests look like jQuery's (X-Requested-With, form-encoded bodies) so Laravel's
 * `$request->ajax()` and validation responses behave the same. Failures reject with a
 * jqXHR-like object: { status, statusText, responseText, responseJSON }.
 */
import { qs, setHTML } from './dom';

function csrfToken() {
    if (typeof window.csrf_token === 'function') return window.csrf_token();
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : '';
}

function toBody(data) {
    if (data == null || data instanceof FormData || data instanceof URLSearchParams || typeof data === 'string') {
        return data ?? undefined;
    }
    const params = new URLSearchParams();
    const append = (key, value) => {
        if (value == null) return;
        if (Array.isArray(value)) value.forEach((v) => append(`${key}[]`, v));
        else if (typeof value === 'object') Object.entries(value).forEach(([k, v]) => append(`${key}[${k}]`, v));
        else params.append(key, value);
    };
    Object.entries(data).forEach(([k, v]) => append(k, v));
    return params;
}

function withQuery(url, data) {
    if (data == null) return url;
    const query = toBody(data).toString();
    if (!query) return url;
    return url + (url.includes('?') ? '&' : '?') + query;
}

async function parse(response) {
    const text = await response.text();
    const type = response.headers.get('content-type') || '';
    let json;
    if (type.includes('json')) {
        try {
            json = text ? JSON.parse(text) : null;
        } catch (e) {
            json = undefined;
        }
    }
    return { text, json, isJson: json !== undefined };
}

/** `$.param(obj)`: URL-encode like jQuery (arrays as key[]=, nested objects as key[k]=). */
export function param(data) {
    return toBody(data || {}).toString();
}

export async function request(url, { method = 'GET', data, headers = {}, signal } = {}) {
    method = method.toUpperCase();
    const isGet = method === 'GET' || method === 'HEAD';
    const response = await fetch(isGet ? withQuery(url, data) : url, {
        method,
        body: isGet ? undefined : toBody(data),
        credentials: 'same-origin',
        signal,
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrfToken(),
            Accept: '*/*',
            ...headers,
        },
    });
    const body = await parse(response);
    if (!response.ok) {
        const error = new Error(`HTTP ${response.status}`);
        Object.assign(error, {
            status: response.status,
            statusText: response.statusText,
            responseText: body.text,
            responseJSON: body.json,
        });
        throw error;
    }
    return body.isJson ? body.json : body.text;
}

/**
 * get/post accept jQuery's shapes too: (url, success), (url, data, success).
 * With a success callback, failures are logged instead of rejecting (like $.get without .fail()).
 */
function shorthand(method) {
    return (url, data, success, opts) => {
        if (typeof data === 'function') {
            opts = success;
            success = data;
            data = undefined;
        }
        const p = request(url, { ...opts, method, data });
        if (typeof success !== 'function') return p;
        return p.then(
            (res) => {
                success(res);
                return res;
            },
            (err) => {
                console.error(`${method} ${url} failed`, err);
            },
        );
    };
}

export const get = shorthand('GET');
export const post = shorthand('POST');

/**
 * $.ajax(options) replacement: url, type/method, data, headers, success(data), error(xhr), complete(),
 * async (false = synchronous request, still used by a few grid renderers). Uses XMLHttpRequest
 * because fetch cannot run synchronously.
 */
export function ajax(options = {}) {
    const method = String(options.type || options.method || 'GET').toUpperCase();
    const isGet = method === 'GET' || method === 'HEAD';
    const body = isGet ? null : toBody(options.data);
    const url = isGet ? withQuery(options.url, options.data) : options.url;
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, options.async !== false);
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.setRequestHeader('X-CSRF-TOKEN', csrfToken());
    xhr.setRequestHeader('Accept', '*/*');
    if (body instanceof URLSearchParams || typeof body === 'string') {
        xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
    }
    Object.entries(options.headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));

    const finish = () => {
        const type = xhr.getResponseHeader('content-type') || '';
        let json;
        if (type.includes('json') || options.dataType === 'json') {
            try {
                json = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            } catch (e) {
                json = undefined;
            }
        }
        Object.assign(xhr, { responseJSON: json });
        if (xhr.status >= 200 && xhr.status < 300) {
            if (options.success) options.success(json !== undefined ? json : xhr.responseText, 'success', xhr);
        } else if (options.error) {
            options.error(xhr, 'error', xhr.statusText);
        } else {
            console.error(`${method} ${options.url} failed`, xhr.status);
        }
        if (options.complete) options.complete(xhr);
    };
    if (options.async !== false) xhr.onloadend = finish;
    xhr.send(body instanceof URLSearchParams ? body.toString() : body);
    if (options.async === false) finish();
    return xhr;
}

/**
 * Submit a form over AJAX using its action/method, like jquery-form's ajaxSubmit.
 * Honours Laravel's `_method` spoofing field because FormData includes it.
 */
export function submitForm(form, { url, method } = {}) {
    const el = qs(form);
    return request(url || el.getAttribute('action') || window.location.href, {
        method: method || el.getAttribute('method') || 'POST',
        data: new FormData(el),
    });
}

/**
 * Submit a form over AJAX now, like jquery-form's `$(form).ajaxSubmit(options)`.
 * Options: beforeSubmit(formData, form), success(data, status), error(xhr), complete(), type/url.
 */
export function ajaxSubmit(form, options = {}) {
    const el = qs(form);
    if (!el) return Promise.resolve();
    const data = new FormData(el);
    if (options.beforeSubmit && options.beforeSubmit(data, el, options) === false) return Promise.resolve();
    return request(options.url || el.getAttribute('action') || window.location.href, {
        method: options.type || options.method || el.getAttribute('method') || 'POST',
        data,
    })
        .then((res) => options.success && options.success(res, 'success'))
        .catch((err) => {
            if (options.error) options.error(err);
            else console.error(err);
        })
        .finally(() => options.complete && options.complete());
}

/**
 * Turn a form into an AJAX form, like `$(form).ajaxForm(options)` (same options as ajaxSubmit).
 * Returns an unbind function.
 */
export function ajaxForm(form, options = {}) {
    const el = qs(form);
    if (!el) return () => {};
    const handler = (e) => {
        e.preventDefault();
        ajaxSubmit(el, options);
    };
    el.addEventListener('submit', handler);
    return () => el.removeEventListener('submit', handler);
}

/** Load a URL's HTML into an element and run its scripts, like `$(el).load(url, cb)`. */
export async function loadInto(target, url, callback) {
    const el = qs(target);
    const html = await request(url, { headers: { Accept: 'text/html, */*; q=0.01' } });
    if (el) setHTML(el, typeof html === 'string' ? html : JSON.stringify(html));
    if (callback) callback.call(el, html);
    return html;
}
